import styles from './AmbientField.module.css';

/**
 * Design system § 4 `grad/ambient` — the background atmosphere, and § 8's L0,
 * which is defined as `background` **plus** `grad/ambient`.
 *
 * Blurred organic blobs drifting behind everything. This is the one decorative
 * gesture § 1.10 allows a screen, and the field is a single object rather than
 * loose blobs so the two rules that govern it cannot be broken one blob at a
 * time:
 *
 * - **Maximum three per screen** (§ 4). The field IS the screen's allowance, so
 *   `count` cannot exceed it and there is no way to add a fourth.
 * - **All outside the reading path** (§ 4), because § 12 prohibits text over an
 *   ambient blob outright. The placements live in the stylesheet, hanging off
 *   the edges of the field, and are not a caller's choice.
 *
 * Purely decorative, so it is hidden from assistive technology (§ 10, § 12) and
 * takes no pointer events. It fills its nearest positioned ancestor: give the
 * host `position: relative`, or use `Surface layer={0}`, which is positioned
 * for exactly this. A page-level host passes `anchor="viewport"` as well, so
 * the atmosphere is measured against the screen rather than against however
 * far the content happens to run.
 */

interface AmbientFieldProps {
  /** How many of the three blobs to render. Fewer for a smaller surface. */
  count?: 1 | 2 | 3;
  /**
   * What the placements are measured against. `surface` is the host's own
   * box — a card. `viewport` is § 8's L0: the page's atmosphere belongs to
   * the screen being looked at, not to the height of the document.
   */
  anchor?: 'surface' | 'viewport';
}

export function AmbientField({ count = 3, anchor = 'surface' }: AmbientFieldProps) {
  return (
    <span className={styles.field} data-anchor={anchor} aria-hidden="true">
      {Array.from({ length: count }, (_, index) => (
        <span key={index} className={styles.blob} data-blob={index + 1} />
      ))}
    </span>
  );
}
